/* eslint-disable no-unused-vars */
import { CheckCircleRounded, Close, ErrorRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Scrollbar } from 'app/components';
import { InspectionIcon } from 'app/icons';
import React from 'react';

function formatInspectionModeLabel(mode) {
  const options = config.clientUi === 'voykan' ? config.inspectionModeOptionsVoykan : config.inspectionModeOptions;
  const matched = options.find((item) => item.value === (mode || ''));
  return matched?.title || options.find((item) => item.value === '')?.title || '';
}

const TransportInspectionDialog = (props) => {
  const { open, onClose, defaultValues } = props;

  if (!defaultValues) {
    return null;
  }

  return (
    <Dialog
      maxWidth='md'
      fullWidth
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.MuiDialog-paper': {
          minWidth: '500px',
        },
        '.MuiDialogTitle-root': {
          display: 'flex',
          padding: theme.spacing(2),
          alignItems: 'center',
        },
        '.MuiDialogTitle-root .MuiTypography-root': {
          marginLeft: theme.spacing(1.5),
        },
        '.MuiDialogTitle-root .MuiButtonBase-root': {
          marginLeft: 'auto',
        },
        '.MuiDialogContent-root': {
          padding: theme.spacing(0),
        },
        '.MuiDialogContent-root .simplebar-container': {
          padding: theme.spacing(0, 2),
        },
        '.MuiDialogActions-root': {
          padding: theme.spacing(2),
          justifyContent: 'flex-end',
        },
        '.MuiAvatar-circular': {
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          svg: {
            width: '18px',
            height: '18px',
          },
          'path, circle': {
            fill: theme.palette.primary.main,
          },
        },
        '.MuiAlert-root': {
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(2),
          borderRadius: theme.shape.borderRadius,
        },
      })}>
      <DialogTitle>
        <Avatar>
          <InspectionIcon fontSize='small' />
        </Avatar>
        <Typography component='p'>Информация об осмотре</Typography>
        <IconButton color='default' onClick={onClose}>
          <Close fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Scrollbar>
          <List
            sx={(theme) => ({
              '.MuiListItem-root': {
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: theme.spacing(1),
                marginBottom: theme.spacing(2),
                '&[data-error="true"]': {
                  borderColor: theme.palette.error.main,
                },
                '&:last-of-type': {
                  marginBottom: 0,
                },
                '.MuiListItemText-root:nth-of-type(1) span': {
                  color: theme.palette.text.secondary,
                },
                '.MuiListItemText-root:nth-of-type(2)': {
                  textAlign: 'right',
                },
              },
            })}>
            <ListItem>
              <ListItemText primary='Название осмотра' />
              <ListItemText primary={defaultValues.name} />
            </ListItem>
            <ListItem>
              <ListItemText primary='Режим осмотра' />
              <ListItemText primary={formatInspectionModeLabel(defaultValues.inspection_mode)} />
            </ListItem>
            <ListItem>
              <ListItemText primary='Время проведения осмотра' />
              <ListItemText primary={moment(defaultValues.created_at).format('DD.MM.YYYY HH:mm:ss')} />
            </ListItem>
            {defaultValues.checkpoints.map(([type, title, values, required], index) => {
              const status = required ? !defaultValues.results[index] : false;

              switch (type) {
                case 'number':
                case 'select':
                  return (
                    <ListItem data-error={status} key={index}>
                      <ListItemText primary={title} />
                      <ListItemText primary={defaultValues.results[index]} />
                    </ListItem>
                  );
                case 'textarea':
                  return (
                    <ListItem
                      key={index}
                      data-error={status}
                      sx={(theme) => ({
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: 0,
                        overflow: 'hidden',
                        '.MuiListItemText-root': {
                          width: '100%',
                          paddingTop: '8px',
                          paddingBottom: '12px',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          marginBottom: 0,
                          borderBottom: '1px solid',
                          borderColor: theme.palette.divider,
                        },
                        'p.MuiTypography-root': {
                          paddingTop: '8px',
                          paddingBottom: '12px',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                        },
                      })}>
                      <ListItemText primary={title} />
                      <Typography component='p'>{defaultValues.results[index]}</Typography>
                    </ListItem>
                  );
                case 'photo':
                  return (
                    <ListItem
                      key={index}
                      data-error={status}
                      sx={(theme) => ({
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: 0,
                        overflow: 'hidden',
                        '.MuiListItemText-root': {
                          width: '100%',
                          paddingTop: '8px',
                          paddingBottom: '12px',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          marginBottom: 0,
                          borderBottom: '1px solid',
                          borderColor: theme.palette.divider,
                        },
                      })}>
                      <ListItemText primary={title} />
                      <Box
                        component='div'
                        sx={{
                          width: '100%',
                          height: '200px',
                          bgcolor: 'black',
                          'a, img': {
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          },
                        }}>
                        <a href={`${location.origin}/resources/${defaultValues.results[index]}`} target='_blank'>
                          <img src={`${location.origin}/resources/${defaultValues.results[index]}`} alt='' />
                        </a>
                      </Box>
                    </ListItem>
                  );
                case 'boolean':
                  return (
                    <ListItem data-error={status} key={index}>
                      <ListItemText primary={title} />
                      {defaultValues.results[index] ? (
                        <CheckCircleRounded fontSize='small' color='success' />
                      ) : (
                        <ErrorRounded fontSize='small' color='error' />
                      )}
                    </ListItem>
                  );
                default:
                  return null;
              }
            })}
          </List>
        </Scrollbar>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant='contained' color='primary' onClick={onClose} disableElevation>
          Закрыть
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default TransportInspectionDialog;
